import { useCallback, useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchShowcaseProjects, isApiEnabled, type ShowcaseProject, type ShowcaseProjectsResponse } from '../lib/api';
import { track } from '../lib/analytics';
import { useProjectsStore } from '../store/useProjectsStore';
import { useAppStore } from '../store/useAppStore';
import { ProjectCard } from '../components/showcase/ProjectCard';
import { FilterBar } from '../components/showcase/FilterBar';
import { FeedTabs } from '../components/FeedTabs';
import { SearchBar } from '../components/showcase/SearchBar';
import { SkeletonProjectCard } from '../components/SkeletonProjectCard';
import { Reveal } from '../components/ui/Reveal';
import { EmptyState } from '../components/EmptyState';

const PAGE_SIZE = 20;

export function ShowcaseScreen() {
  const { filters, setFilters } = useProjectsStore();
  const setScreen = useAppStore((s) => s.setScreen);
  const setSelectedProjectId = useAppStore((s) => s.setSelectedProjectId);
  const createProject = useAppStore((s) => s.createProject);

  const search = filters.search ?? '';
  const category = filters.category ?? null;

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['showcase', search, category],
    queryFn: async ({ pageParam }) => {
      return fetchShowcaseProjects({
        cursor: pageParam ?? undefined,
        limit: PAGE_SIZE,
        search: search || undefined,
        category: category ?? undefined,
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: isApiEnabled(),
  });

  useEffect(() => {
    track('showcase_opened');
  }, []);

  useEffect(() => {
    if (error) console.error('[ShowcaseScreen] API error:', error);
  }, [error]);

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void fetchNextPage();
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleOpenProject = useCallback((id: string) => {
    setSelectedProjectId(id);
    setScreen('detail');
    useAppStore.getState().setDetailSource('public');
  }, [setScreen, setSelectedProjectId]);

  const handleSearchChange = useCallback(
    (value: string) => setFilters((prev) => ({ ...prev, search: value })),
    [setFilters]
  );
  const handleCategoryChange = useCallback(
    (value: string | null) => setFilters((prev) => ({ ...prev, category: value })),
    [setFilters]
  );

  const rawItems = data?.pages?.flatMap((p) => (p as ShowcaseProjectsResponse).items ?? []) ?? [];
  const items = rawItems.filter((p): p is ShowcaseProject => p != null && typeof p.id === 'string');
  const isEmpty = !isLoading && items.length === 0;
  const showEmpty = isEmpty && isApiEnabled();
  const showUnavailable = !isApiEnabled();

  return (
    <div className="showcase-screen">
      <FeedTabs />
      <header className="header">
        <h1 className="header-title">Витрина</h1>
        <p className="header-subtitle">Публичные проекты</p>
      </header>

      <main className="main">
        <div className="container">
          <SearchBar value={search} onChange={handleSearchChange} />
          <FilterBar
            category={category}
            onCategoryChange={handleCategoryChange}
            isFetching={isFetching && !isLoading}
          />

          {showUnavailable && (
            <div className="showcase-empty">
              <p className="showcase-empty__title">API не подключён</p>
              <p>Настройте API для загрузки проектов</p>
            </div>
          )}

          {showEmpty && !showUnavailable && (
            <EmptyState
              type={search || category ? 'search' : 'showcase'}
              onAction={search || category ? () => setFilters({}) : createProject ?? undefined}
            />
          )}

          {!showUnavailable && (
            <>
              {error && (
                <div style={{
                  padding: '12px 16px',
                  margin: '0 0 12px',
                  borderRadius: 12,
                  background: 'rgba(248,113,113,0.1)',
                  border: '1px solid rgba(248,113,113,0.2)',
                  color: 'rgba(248,113,113,0.9)',
                  fontSize: '0.8125rem',
                }}>
                  Не удалось загрузить проекты. Попробуйте обновить страницу.
                </div>
              )}
              {isLoading
                ? Array.from({ length: 3 }, (_, i) => <SkeletonProjectCard key={i} />)
                : items.map((project, i) => (
                    <Reveal key={project.id} delay={i * 60}>
                      <ProjectCard
                        project={project}
                        onClick={() => handleOpenProject(project.id)}
                      />
                    </Reveal>
                  ))}
              {isFetchingNextPage && <SkeletonProjectCard />}
              <div ref={sentinelRef} aria-hidden style={{ height: 1, marginTop: 0 }} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
